<template>
    <div v-bind:class="{ 'big-event-theme': isJackpotEFootballInBigEventTheme }">
        <div class="jackpotBlock-wrapper">
            <picture>
                <source
                    class="jackpotBlock-bg"
                    v-bind:srcset="jackpotHeaderStarsWebp"
                    type="image/webp"
                />
                <img
                    class="jackpotBlock-bg"
                    v-bind:src="jackpotHeaderStarsPng"
                    alt="jackpot"
                />
            </picture>
            <div
                v-if="isLoaded"
                v-gtm:type="'PrizePool'"
                v-gtm:page="gtmPage"
                v-gtm:@view
                class="jackpotBlock"
            >
                <div class="info-column">
                    <div
                        v-gtm:type="'PrizePool'"
                        v-gtm:page="gtmPage"
                        v-gtm:label="gtmLabel"
                        v-gtm:@click
                        class="info-toggle"
                        v-on:click="toggleExpand"
                    >
                        <template v-if="matchCount">
                            <div class="info-header-container">
                                <div class="info-header">
                                    <SvgIcon name="icon-system-e-football" size="lg" />
                                    <span class="info-header-label">{{ title }}</span>
                                    <Count
                                        v-bind:number="matchCount"
                                        v-bind:bgColor="countBgColor"
                                        v-bind:textColor="'white'"
                                    />
                                </div>
                                <picture>
                                    <source
                                        v-bind:srcset="jackpotImage.webp"
                                        type="image/webp"
                                    />
                                    <img
                                        class="info-header-coin"
                                        v-bind:src="jackpotImage.png"
                                        alt=""
                                    />
                                </picture>
                            </div>
                            <div class="info-img" />
                        </template>

                        <template v-else>
                            <div class="jackpotBlock-fullTitle">
                                <picture>
                                    <source
                                        v-bind:srcset="jackpotPromoteTitleImage.webp"
                                        type="image/webp"
                                    />
                                    <img
                                        v-bind:src="jackpotPromoteTitleImage.png"
                                        alt=""
                                    />
                                </picture>
                            </div>
                        </template>
                    </div>
                    <div v-if="isExpanded" class="info-detail">
                        <div class="info-detail-title">
                            {{ howItWorks.title }}
                        </div>
                        <ol v-bind:class="{ 'bengali-numbers': isBengali }">
                            <li>{{ howItWorks.step1 }}</li>
                            <li>{{ howItWorks.step2 }}</li>
                            <li>{{ howItWorks.step3 }}</li>
                        </ol>
                        <div class="text-button">
                            <VButton
                                v-gtm:type="'T&C'"
                                v-gtm:page="gtmPage"
                                v-gtm:@click
                                underline
                                v-on:click="openTncLink"
                            >
                                {{ tncs }}
                            </VButton>
                        </div>
                    </div>
                </div>
                <JackpotPoolContent
                    v-for="pool in poolSummary"
                    v-bind:key="pool.poolType"
                    v-bind:isExpanded="isExpanded"
                    v-bind:isShowArrow="false"
                    v-bind:pool="pool"
                    v-on:toggle-expand="toggleExpand"
                />
                <div class="arrow-icon">
                    <SboIcon
                        v-gtm:type="'PrizePool'"
                        v-gtm:page="gtmPage"
                        v-gtm:label="gtmLabel"
                        v-gtm:@click
                        name="system:arrowDown"
                        size="lg"
                        v-bind:rotated="isExpanded"
                        v-on:click="toggleExpand"
                    />
                </div>
            </div>

            <div v-else class="loadingOverlay">
                <StaticSpinner color="var(--jackpotLoadingColor)" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, ref } from 'vue';
    import { useRoute } from 'vue-router';
    import jackpotHeaderStarsPng from '@/assets/images/jackpot/bg-jackpotHeader-stars.png';
    import jackpotHeaderStarsWebp from '@/assets/images/jackpot/bg-jackpotHeader-stars.webp';
    import jackpotFullTitleBigEventPng from '@/assets/images/jackpot/img-fulltitle-jackpot-bigEvent.png';
    import jackpotFullTitleBigEventWebp from '@/assets/images/jackpot/img-fulltitle-jackpot-bigEvent.webp';
    import jackpotEFootballPromoteTitlePng from '@/assets/images/jackpot/img-title-e-football-promote.png';
    import jackpotEFootballPromoteTitleWebp from '@/assets/images/jackpot/img-title-e-football-promote.webp';
    import jackpotCoinBigEventPng from '@/assets/images/jackpot/img-title-jackpotCoin-bigEvent.png';
    import jackpotCoinBigEventWebp from '@/assets/images/jackpot/img-title-jackpotCoin-bigEvent.webp';
    import jackpotCoinPng from '@/assets/images/jackpot/img-title-jackpotCoin.png';
    import jackpotCoinWebp from '@/assets/images/jackpot/img-title-jackpotCoin.webp';
    import jackpotPromoteTitlePng from '@/assets/images/jackpot/img-title-promote.png';
    import jackpotPromoteTitleWebp from '@/assets/images/jackpot/img-title-promote.webp';
    import Count from '@/components/common/Count.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import VButton from '@/components/common/button/VButton.vue';
    import JackpotPoolContent from '@/components/jackpot/JackpotPoolContent.vue';
    import { usePoolSummary } from '@/composable/useJackpotData';
    import { getJackpotTncUrl } from '@/core/lib/helpCenter';
    import { i18n } from '@/core/lib/i18n';
    import { AsiaRoutePage, EnumLanguage, SiteStyle } from '@/interface/enum';
    import type { IColors } from '@/interface/sassVariables';
    import { useCustomerStore } from '@/store/customerStore';
    import { useToggleStore } from '@/store/toggleStore';

    defineProps<{
        matchCount?: number;
        countBgColor?: keyof IColors;
    }>();

    const { brandType, isMainDomain } = storeToRefs(useCustomerStore());
    const { isJackpotEFootballInBigEventTheme, isSwitchEFootballToSimulationSportsZoneEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));

    if (isJackpotEFootballInBigEventTheme.value) {
        import('./JackpotBlockBigEvent.scss');
    } else {
        import('./JackpotBlock.scss');
    }

    const tncUrl = computed(() => getJackpotTncUrl(brandType.value, isMainDomain.value));

    const openTncLink = () => {
        window.open(tncUrl.value, '_blank', 'noopener,noreferrer');
    };

    const { poolSummary } = usePoolSummary();

    const route = useRoute();

    const isJackpotRoute = computed(() => route.name === AsiaRoutePage.Jackpot);
    const isExpanded = ref(isJackpotRoute.value);

    const toggleExpand = () => {
        isExpanded.value = !isExpanded.value;
    };

    const isLoaded = computed(() => poolSummary.value.length > 0);

    const isBengali = computed(() => i18n.locale.value === EnumLanguage.BN_BD);

    const gtmLabel = computed(() => (isExpanded.value ? 'Off' : 'On'));
    const gtmPage = computed(() => (isSwitchEFootballToSimulationSportsZoneEnabled.value ? 'K_JackPot' : 'K_Efootball_JP'));

    const jackpotImage = computed(() => (isJackpotEFootballInBigEventTheme.value
        ? { webp: jackpotCoinBigEventWebp, png: jackpotCoinBigEventPng }
        : { webp: jackpotCoinWebp, png: jackpotCoinPng }));

    const jackpotPromoteTitleImage = computed(() => {
        if (isJackpotEFootballInBigEventTheme.value) {
            return { webp: jackpotFullTitleBigEventWebp, png: jackpotFullTitleBigEventPng };
        }

        return isSwitchEFootballToSimulationSportsZoneEnabled.value
            ? { webp: jackpotPromoteTitleWebp, png: jackpotPromoteTitlePng }
            : { webp: jackpotEFootballPromoteTitleWebp, png: jackpotEFootballPromoteTitlePng };
    });

    const title = computed(() => i18n.t('promotion_zone_2'));
    const howItWorks = computed(() => ({
        title: i18n.t('jackpot_how_it_works'),
        step1: isSwitchEFootballToSimulationSportsZoneEnabled.value ? i18n.t('cyber_sports_jackpot_how_it_works_1') : i18n.t('jackpot_how_it_works_1'),
        step2: i18n.t('jackpot_how_it_works_2'),
        step3: i18n.t('jackpot_how_it_works_3'),
    }));
    const tncs = computed(() => i18n.t('jackpot_tncs'));
</script>
